import {
  getCardOnFilePaymentMethodAdditionalInfo,
  getTilledPaymentErrorCodeSolution,
  getTilledUserFriendlyErrorMessage,
  isTilledPaymentErrorDetail,
  PaymentMethod,
  PaymentMethodBillingInfoDto,
  paymentIntervalToSubscriptionInterval,
  ThisShouldNeverHappenError,
  TilledPaymentErrorDetail,
  tokenizedPaymentMethodAccountMismatchMessage,
} from '@breezy/shared'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '../../../../elements/Button'
import Switch from '../../../../elements/Switch/Switch'
import { useFetchCardsOnFile } from '../../../../hooks/fetch/useFetchCardsOnFile'
import { trpc } from '../../../../hooks/trpc'
import { SetIsDirty } from '../../../../hooks/useExternalIsDirty'
import { useExpectedCompanyGuid, useExpectedCompanyTimeZoneId } from '../../../../providers/PrincipalUser'
import { CloseConfirmModal, useCloseConfirmModal } from '../../../../ui/OnsiteModal/useCloseConfirmModal'
import { useMessage } from '../../../../utils/antd-utils'
import GqlQueryLoader from '../../../GqlQueryLoader/GqlQueryLoader'
import { useCreatePaymentMethodRecordFromSubscription } from '../../hooks/useCreatePaymentMethodFromSubscription'
import { useCreateTilledPaymentMethod } from '../../hooks/useCreateTilledPaymentMethod'
import {
  getDefaultBillingAddress,
  getDefaultContactName,
  useDefaultAccountBillingInfo,
} from '../../hooks/useDefaultAccountBillingInfo'
import { useMaintenancePlanSubscriptionAttempt } from '../../hooks/useMaintenancePlanSubscriptionAttempt'
import { useResolvedTilledAccount } from '../../hooks/useResolvedTilledAccount'
import { isCreditCardFormData, PaymentFormData } from '../../hooks/useSubmitTilledPayment'
import { useTilledPaymentForm } from '../../hooks/useTilledPaymentForm'
import { MaintenancePlanActivationInput, usePaymentWorkflowContext } from '../../PaymentWorkflowWizard'
import { usePaymentStatusModalContext } from '../../providers/PaymentStatusModalProvider'
import { AchPaymentForm } from '../AchPaymentForm/AchPaymentForm'
import { CreditCardPaymentForm } from '../CreditCardPaymentForm/CreditCardPaymentForm'
import { TilledAccountResolutionError } from '../TilledAccountResolutionError/TilledAccountResolutionError'
import { getNameOfPayer, isPaymentFormData } from '../TilledPaymentWorkflow/TilledPaymentWorkflow'

type TilledPaymentSubscriptionWorkflowProps<PaymentMethodType extends PaymentMethod.CARD | PaymentMethod.ACH> = {
  maintenancePlan: MaintenancePlanActivationInput
  onCancel?: (force?: boolean) => void
  paymentMethod: PaymentMethodType
  setIsDirty?: SetIsDirty
}

type TilledPaymentSubscriptionFormProps<PaymentMethodType extends PaymentMethod.CARD | PaymentMethod.ACH> =
  TilledPaymentSubscriptionWorkflowProps<PaymentMethodType> & {
    subscriptionAttempt: ReturnType<typeof useMaintenancePlanSubscriptionAttempt>
  }

const getPaymentMethodBillingInfo = (
  formData: PaymentFormData<PaymentMethod.CARD | PaymentMethod.ACH>,
): PaymentMethodBillingInfoDto => {
  const name = isCreditCardFormData(formData) ? formData.name : formData.accountHolderName
  if (!name || !formData.streetAddress || !formData.city || !formData.state || !formData.zipCode) {
    throw new ThisShouldNeverHappenError('Missing billing info during payment method finalization')
  }
  return {
    name,
    streetAddress: formData.streetAddress,
    streetAddress2: formData.streetAddress2,
    city: formData.city,
    state: formData.state,
    zipCode: formData.zipCode,
  }
}

const isTokenizedPaymentMethodAccountMismatch = (error: unknown): boolean =>
  error instanceof Error && error.message === tokenizedPaymentMethodAccountMismatchMessage

const TilledPaymentSubscriptionForm = <PaymentMethodType extends PaymentMethod.CARD | PaymentMethod.ACH>({
  maintenancePlan,
  onCancel,
  paymentMethod,
  setIsDirty: externalSetIsDirty,
  subscriptionAttempt,
}: TilledPaymentSubscriptionFormProps<PaymentMethodType>) => {
  const message = useMessage()
  const { showPaymentStatusModal } = usePaymentStatusModalContext()
  const { mode, onPaymentSuccess, onPaymentError } = usePaymentWorkflowContext()
  const companyGuid = useExpectedCompanyGuid()
  const tzId = useExpectedCompanyTimeZoneId()
  const [selectedPaymentMethodRecordGuid, setSelectedPaymentMethodRecordGuid] = useState<string>()
  const resolvedAccount = useResolvedTilledAccount({
    companyGuid,
    paymentMethodRecordGuid: selectedPaymentMethodRecordGuid,
    authenticated: true,
  })
  const tilledMerchantId = resolvedAccount.merchantId
  const {
    data: cardsOnFile,
    refetch: refetchCardsOnFile,
    fetching: isLoadingCardsOnFile,
  } = useFetchCardsOnFile(maintenancePlan.accountGuid)

  const paymentAmountUsc = maintenancePlan.totalPriceUsc

  // A ref is used to store the submitted card data so it can be used in the useEffect below
  const submittedPaymentData = useRef<PaymentFormData<PaymentMethodType> | undefined>(undefined)
  const { attempt, clear: clearSubscriptionAttempt, retainOperation } = subscriptionAttempt
  const paymentSubscriptionGuid = attempt.paymentSubscriptionGuid

  const activateMaintenancePlan = trpc.maintenancePlans['maintenance-plans:create-payment-subscription'].useMutation()

  const accountBillingInfoQuery = useDefaultAccountBillingInfo(maintenancePlan.accountGuid)

  const {
    cardNumberElement,
    cardExpirationElement,
    cardCvvElement,
    accountNumberElement,
    routingNumberElement,
    loadingTilled,
    tilledError,
    tilledFormInfo,
    onSubmitValidationCheck,
  } = useTilledPaymentForm(paymentMethod, tilledMerchantId)

  const onSubmitPaymentError = useCallback(
    (error: TilledPaymentErrorDetail | Error) => {
      const submittedData = submittedPaymentData.current
      if (!submittedData) return
      console.error(error)
      const isTilledError = isTilledPaymentErrorDetail(error)

      if (isTilledError) {
        // Show the Payment Completed Modal w/ the success state
        showPaymentStatusModal({
          mode,
          type: 'payment-error',
          data: {
            nameOfPayer: getNameOfPayer(submittedData),
            paymentAmountUsc,
            paymentMethod,
            errorMessage: getTilledUserFriendlyErrorMessage(error?.message),
            errorCode: error.code,
            solution: getTilledPaymentErrorCodeSolution(paymentMethod, error.code),
          },
        })
        onPaymentError?.()
      } else {
        showPaymentStatusModal({
          mode,
          type: 'payment-error',
          data: {
            nameOfPayer: getNameOfPayer(submittedData),
            paymentAmountUsc,
            paymentMethod,
            errorMessage: error?.message,
          },
        })
        onPaymentError?.()
      }
      refetchCardsOnFile()
    },
    [refetchCardsOnFile, showPaymentStatusModal, mode, paymentMethod, onPaymentError, paymentAmountUsc],
  )

  const {
    createPaymentMethod,
    updatePaymentMethod,
    isLoading: isCreatingPaymentMethod,
  } = useCreateTilledPaymentMethod({
    accountGuid: maintenancePlan.accountGuid,
    tilledFormInfo,
    paymentMethod,
    onError: onSubmitPaymentError,
  })

  const { createPaymentMethodRecord, isLoading: isCreatingPaymentMethodRecord } =
    useCreatePaymentMethodRecordFromSubscription({
      accountGuid: maintenancePlan.accountGuid,
      paymentMethod,
    })

  useEffect(() => {
    if (!loadingTilled && tilledError) {
      console.error(tilledError)
      message.error('Something went wrong. Try again later.')
    }
  }, [loadingTilled, message, tilledError])

  const { setIsProcessingPayment } = usePaymentWorkflowContext()
  const loading =
    resolvedAccount.isLoading ||
    loadingTilled ||
    isCreatingPaymentMethod ||
    isLoadingCardsOnFile ||
    isCreatingPaymentMethodRecord

  const onSubmitInner = useCallback(
    async (data: object) => {
      if (!isPaymentFormData<PaymentMethodType>(data)) throw new ThisShouldNeverHappenError('Invalid payment form data')
      if (!tilledMerchantId) throw resolvedAccount.error ?? new Error('No Tilled account is available for this plan')
      // This is critical to prevent the disruption of the payment workflow.
      // It effectively locks down the navigation of the payment workflow modal
      // and prevents the user from clicking the back button, cancel button, closing the modal w/ a background click, or closing/navigating from the page
      setIsProcessingPayment(true)

      submittedPaymentData.current = data

      try {
        let paymentMethodForOperation = attempt.retainedPaymentMethod

        if (!paymentMethodForOperation && isCreditCardFormData(data) && data.selectedCardOnFile) {
          const selectedCard = data.selectedCardOnFile
          paymentMethodForOperation = {
            externalPaymentMethodId: selectedCard.externalPaymentMethodId,
            paymentMethodRecordGuid: selectedCard.paymentMethodRecordGuid,
            paymentMethodAdditionalInfo: getCardOnFilePaymentMethodAdditionalInfo(selectedCard),
            paymentMethod,
          }

          const currentCardOnFile = cardsOnFile?.find(
            card => card.paymentMethodRecordGuid === selectedCard.paymentMethodRecordGuid,
          )
          const address = currentCardOnFile?.paymentMethodBillingInfo?.address
          if (address) {
            if (
              data.streetAddress !== address.line1 ||
              data.streetAddress2 !== address.line2 ||
              data.city !== address.city ||
              data.state !== address.stateAbbreviation ||
              data.zipCode !== address.zipCode
            ) {
              try {
                await updatePaymentMethod(
                  selectedCard.paymentMethodRecordGuid,
                  data,
                  companyGuid,
                  selectedCard.externalPaymentMethodId,
                  false,
                )
              } catch (error) {
                console.error('Plan activation will continue without updating the saved-card billing address', error)
              }
            }
          }
        } else if (!paymentMethodForOperation) {
          const res = await createPaymentMethod(data, companyGuid, false, true, false)
          if (!res) {
            // throw an error about not being able to create the payment method
            throw new Error('Failed to create payment method')
          }
          paymentMethodForOperation = { ...res, paymentMethod }
        }

        const paymentMethodForAttempt = paymentMethodForOperation.paymentMethod
        try {
          const retainedOperation = retainOperation(
            paymentMethodForOperation,
            {
              companyGuid,
              paymentSubscriptionGuid,
              merchantId: tilledFormInfo?.accountId ?? tilledMerchantId,
              paymentMethodId: paymentMethodForOperation.externalPaymentMethodId,
              paymentMethod: paymentMethodForAttempt,
              paymentInterval: paymentIntervalToSubscriptionInterval(maintenancePlan.paymentInterval),
              periodTotalPaymentPriceUsc: maintenancePlan.totalPriceUsc,
              paymentMethodAdditionalInfo: paymentMethodForOperation.paymentMethodAdditionalInfo,

              accountGuid: maintenancePlan.accountGuid,
              activationDate: maintenancePlan.activationDate,
              paymentMethodRecordGuid: paymentMethodForOperation.paymentMethodRecordGuid,
              pricebookTaxRateGuid: maintenancePlan.taxRateGuid ?? null,
              maintenancePlanGuid: maintenancePlan.maintenancePlanGuid,
              customBillingStartDate: maintenancePlan.customBillingStartDate,
            },
            paymentMethodForOperation.paymentMethodRecordGuid
              ? undefined
              : {
                  billingInfo: getPaymentMethodBillingInfo(data),
                  cardMetadata: paymentMethodForOperation.cardMetadata,
                },
          )
          await activateMaintenancePlan.mutateAsync(retainedOperation.activationRequest)

          // If the user isn't paying with a card on file, we need to create a payment method record
          // from the payment subscription that was created when the maintenance plan was activated
          if (!retainedOperation.paymentMethod.paymentMethodRecordGuid) {
            if (!retainedOperation.paymentMethodRecordFinalization) {
              throw new ThisShouldNeverHappenError(
                'Missing saved-card finalization for a newly tokenized payment method',
              )
            }
            await createPaymentMethodRecord(
              retainedOperation.paymentMethodRecordFinalization.billingInfo,
              companyGuid,
              retainedOperation.activationRequest.paymentSubscriptionGuid,
              retainedOperation.paymentMethodRecordFinalization.cardMetadata,
            )
          }

          clearSubscriptionAttempt()
          onPaymentSuccess?.(paymentMethodForAttempt, paymentAmountUsc)
        } catch (err) {
          console.error(err)
          if (isTokenizedPaymentMethodAccountMismatch(err)) {
            clearSubscriptionAttempt()
            void resolvedAccount.retry()
          }
          onSubmitPaymentError(err as Error | TilledPaymentErrorDetail)
          setIsProcessingPayment(false)
        }
      } catch (err) {
        console.error(err)
        onSubmitPaymentError(err as Error | TilledPaymentErrorDetail)
      }
    },
    [
      setIsProcessingPayment,
      createPaymentMethod,
      companyGuid,
      tilledMerchantId,
      resolvedAccount.error,
      updatePaymentMethod,
      activateMaintenancePlan,
      paymentMethod,
      maintenancePlan,
      cardsOnFile,
      onPaymentSuccess,
      paymentAmountUsc,
      createPaymentMethodRecord,
      onSubmitPaymentError,
      paymentSubscriptionGuid,
      tilledFormInfo?.accountId,
      attempt.retainedPaymentMethod,
      retainOperation,
      clearSubscriptionAttempt,
      resolvedAccount.retry,
    ],
  )

  useEffect(() => {
    if (!loadingTilled && tilledError) {
      console.error(tilledError)
      message.error('Something went wrong. Try again later.')
    }
  }, [loadingTilled, message, tilledError])

  const [isDirty, setIsDirty] = useState(false)

  const [withConfirmation, closeConfirmProps] = useCloseConfirmModal({
    isDirty,
  })

  const setPaymentIsDirty = useCallback(
    (isDirty: boolean) => {
      setIsDirty(isDirty)
      externalSetIsDirty?.(isDirty)
    },
    [externalSetIsDirty],
  )

  const onCancelWithConfirmation = useCallback(
    () => (onCancel ? withConfirmation(onCancel) : undefined),
    [onCancel, withConfirmation],
  )

  if (resolvedAccount.error) {
    return (
      <TilledAccountResolutionError
        hasSavedPaymentMethod={!!selectedPaymentMethodRecordGuid}
        onRetry={() => void resolvedAccount.retry()}
        onUseAnotherPaymentMethod={
          selectedPaymentMethodRecordGuid ? () => setSelectedPaymentMethodRecordGuid(undefined) : undefined
        }
      />
    )
  }

  return (
    <>
      <GqlQueryLoader
        query={accountBillingInfoQuery}
        render={data => {
          const defaultBillingAddress = getDefaultBillingAddress(data)
          const defaultContactName = getDefaultContactName(data)
          return (
            <Switch value={paymentMethod}>
              {{
                [PaymentMethod.CARD]: () => (
                  <CreditCardPaymentForm
                    cardsOnFile={cardsOnFile}
                    onSelectedCardOnFileChange={card =>
                      setSelectedPaymentMethodRecordGuid(card?.paymentMethodRecordGuid)
                    }
                    tzId={tzId}
                    primaryButtonText="Activate Plan"
                    name={defaultContactName}
                    streetAddress={defaultBillingAddress?.line1 ?? ''}
                    city={defaultBillingAddress?.city ?? ''}
                    state={defaultBillingAddress?.state ?? ''}
                    zipCode={defaultBillingAddress?.zipCode}
                    isLoading={loading}
                    setIsDirty={setPaymentIsDirty}
                    onSubmitValidationCheck={onSubmitValidationCheck}
                    onSubmit={data =>
                      onSubmitInner({
                        ...data,
                      })
                    }
                    onCancel={onCancel ? onCancelWithConfirmation : undefined}
                    withAddressTypeAhead
                    formStyle="modal"
                  >
                    <div className="flex flex-col gap-2">
                      {cardNumberElement}
                      <div className="flex flex-row space-x-3">
                        {cardExpirationElement}
                        {cardCvvElement}
                      </div>
                    </div>
                  </CreditCardPaymentForm>
                ),
                [PaymentMethod.ACH]: () => (
                  <AchPaymentForm
                    primaryButtonText="Activate Plan"
                    name={defaultContactName}
                    streetAddress={defaultBillingAddress?.line1 ?? ''}
                    city={defaultBillingAddress?.city ?? ''}
                    state={defaultBillingAddress?.state ?? ''}
                    zipCode={defaultBillingAddress?.zipCode}
                    isLoading={loading}
                    setIsDirty={setPaymentIsDirty}
                    onSubmitValidationCheck={onSubmitValidationCheck}
                    onSubmit={data =>
                      onSubmitInner({
                        ...data,
                      })
                    }
                    onCancel={onCancel ? onCancelWithConfirmation : undefined}
                  >
                    <div className="flex flex-row space-x-3">
                      {accountNumberElement}
                      {routingNumberElement}
                    </div>
                  </AchPaymentForm>
                ),
                default: () => null,
              }}
            </Switch>
          )
        }}
      />
      <CloseConfirmModal {...closeConfirmProps} />
    </>
  )
}

type TilledPaymentSubscriptionRecoveryProps<PaymentMethodType extends PaymentMethod.CARD | PaymentMethod.ACH> = Pick<
  TilledPaymentSubscriptionWorkflowProps<PaymentMethodType>,
  'maintenancePlan'
> & {
  subscriptionAttempt: ReturnType<typeof useMaintenancePlanSubscriptionAttempt>
}

const TilledPaymentSubscriptionRecovery = <PaymentMethodType extends PaymentMethod.CARD | PaymentMethod.ACH>({
  maintenancePlan,
  subscriptionAttempt,
}: TilledPaymentSubscriptionRecoveryProps<PaymentMethodType>) => {
  const message = useMessage()
  const { hidePaymentStatusModal } = usePaymentStatusModalContext()
  const { onPaymentError, onPaymentSuccess, setIsProcessingPayment } = usePaymentWorkflowContext()
  const activateMaintenancePlan = trpc.maintenancePlans['maintenance-plans:create-payment-subscription'].useMutation()
  const { activationRequest, retainedPaymentMethod, paymentMethodRecordFinalization } = subscriptionAttempt.attempt
  const { createPaymentMethodRecord } = useCreatePaymentMethodRecordFromSubscription({
    accountGuid: activationRequest?.accountGuid ?? maintenancePlan.accountGuid,
    paymentMethod: retainedPaymentMethod?.paymentMethod ?? PaymentMethod.CARD,
  })

  if (!activationRequest || !retainedPaymentMethod) {
    throw new ThisShouldNeverHappenError('A recoverable subscription attempt must have a complete operation')
  }

  const resume = async () => {
    setIsProcessingPayment(true)
    try {
      await activateMaintenancePlan.mutateAsync(activationRequest)
      if (!retainedPaymentMethod.paymentMethodRecordGuid) {
        if (!paymentMethodRecordFinalization) {
          throw new ThisShouldNeverHappenError('Missing saved-card finalization for a recovered payment method')
        }
        await createPaymentMethodRecord(
          paymentMethodRecordFinalization.billingInfo,
          activationRequest.companyGuid,
          activationRequest.paymentSubscriptionGuid,
          paymentMethodRecordFinalization.cardMetadata,
        )
      }
      subscriptionAttempt.clear()
      onPaymentSuccess?.(retainedPaymentMethod.paymentMethod, maintenancePlan.totalPriceUsc)
    } catch (error) {
      console.error(error)
      hidePaymentStatusModal()
      if (isTokenizedPaymentMethodAccountMismatch(error)) {
        subscriptionAttempt.clear()
        message.error(`${tokenizedPaymentMethodAccountMismatchMessage} The account will be refreshed before retrying.`)
        setIsProcessingPayment(false)
        return
      }
      message.error('The existing plan activation could not be completed. It is safe to try Resume activation again.')
      onPaymentError?.()
      setIsProcessingPayment(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded border border-solid border-gray-200 p-4">
      <p>This activation is waiting to finish. Resume it without entering or creating another payment method.</p>
      <Button type="button" variant="primary" onClick={() => void resume()}>
        Resume activation
      </Button>
    </div>
  )
}

const TilledPaymentSubscriptionWorkflow = <PaymentMethodType extends PaymentMethod.CARD | PaymentMethod.ACH>(
  props: TilledPaymentSubscriptionWorkflowProps<PaymentMethodType>,
) => {
  const subscriptionAttempt = useMaintenancePlanSubscriptionAttempt(props.maintenancePlan.maintenancePlanGuid)
  if (subscriptionAttempt.recoveryError) {
    return (
      <div role="alert" className="rounded border border-solid border-red-300 p-4">
        This activation cannot be safely recovered in this browser. Do not enter another payment method. Contact support
        before retrying.
      </div>
    )
  }
  if (subscriptionAttempt.attempt.retainedPaymentMethod || subscriptionAttempt.attempt.activationRequest) {
    return <TilledPaymentSubscriptionRecovery {...props} subscriptionAttempt={subscriptionAttempt} />
  }
  return <TilledPaymentSubscriptionForm {...props} subscriptionAttempt={subscriptionAttempt} />
}

export default React.memo(TilledPaymentSubscriptionWorkflow) as typeof TilledPaymentSubscriptionWorkflow
